import React from "react";
import {
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@mindstonehq/ui";
import { graphql } from "~/graphql/codegen";
import { useMutation } from "@apollo/client/react";
import { useAIUsage } from "./ai-usage-context";
import type {
  UpdateUserStateStoreAiUsageLastDismissedAtMutation,
  UpdateUserStateStoreAiUsageLastDismissedAtMutationVariables,
} from "~/graphql/codegen/graphql";
import metrics from "~/utils/metrics";
import { Link } from "react-router";
import { useIsDefaultPlatformSpace, useProfile } from "~/hooks/context-hook";
import { Trans, useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";

const UPDATE_USER_STATE_STORE_AI_USAGE_LAST_DISMISSED_AT_MUTATION = graphql(`
  mutation UpdateUserStateStoreAiUsageLastDismissedAt(
    $aiUsageLastDismissedAt: String
  ) {
    updateUserStateStore(aiUsageLastDismissedAt: $aiUsageLastDismissedAt) {
      aiUsageLastDismissedAt
    }
  }
`);

import aiUsageHeroPng from "~/assets/ai-usage-hero.png";

// Illustration exported from Figma
const heroImg = aiUsageHeroPng;

export function GlobalAIUsageReminderPopover() {
  const { t } = useLingui();
  const myProfile = useProfile();
  const isDefaultPlatformSpace = useIsDefaultPlatformSpace();
  const { showAIUsagePopover, setShowAIUsagePopover } = useAIUsage();

  const [updateDismissed] = useMutation<
    UpdateUserStateStoreAiUsageLastDismissedAtMutation,
    UpdateUserStateStoreAiUsageLastDismissedAtMutationVariables
  >(UPDATE_USER_STATE_STORE_AI_USAGE_LAST_DISMISSED_AT_MUTATION);
  const handleDismiss = () => {
    setShowAIUsagePopover(false);
    updateDismissed({
      variables: {
        aiUsageLastDismissedAt: new Date().toISOString(),
      },
    });
    metrics.track("ai_usage_popover_dismissed");
  };

  if (!showAIUsagePopover) return <></>;

  return (
    <div className="fixed top-8 right-[40px] z-50">
      <Popover
        open={showAIUsagePopover}
        onOpenChange={(open) => {
          if (!open) {
            handleDismiss();
          } else {
            setShowAIUsagePopover(open);
          }
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="w-0 h-0 p-0 m-0 overflow-hidden"
          />
        </PopoverTrigger>
        <PopoverContent side="bottom" align="end" className="w-[340px]">
          <div className="flex flex-col items-center justify-center text-center gap-2">
            {/* Header */}
            <p className="font-bold text-base text-foreground leading-6">
              <Trans>See your AI growth. Show your impact.</Trans>
            </p>

            {/* Body copy */}
            <p className="text-xs text-muted-foreground leading-5">
              <Trans>It’s time to take 2 minutes to log how you’ve used or learned about AI this month.</Trans>{" "}
              {isDefaultPlatformSpace
                ? t(msg`Your data feeds into your graph and you can compare against other Mindstone users.`)
                : t(msg`Your data feeds into your graph and is visible to your employer.`)}
            </p>
            <p className="text-xs text-placeholder-foreground leading-5 py-2">
              <Trans>Go to your user profile or click on the button below.</Trans>
            </p>

            {/* CTA */}
            <Link to={`/profile/${myProfile?.username}?tab=ai-usage`}>
              <Button size="sm" className="bg-primary text-primary-foreground">
                <Trans>Log this month’s AI growth</Trans>
              </Button>
            </Link>

            {/* Illustration */}
            <img
              src={heroImg}
              alt={t(msg`Illustration showing AI progress graph`)}
              className="w-full rounded-md object-cover mt-2"
            />
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
